import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, HeartHandshake, Target, Users, ArrowRight, Lightbulb, Heart } from 'lucide-react';
import { AnimatedCounter } from '../components/AnimatedCounter';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const values = [
  { icon: Users,        title: 'Customer Centricity', desc: 'Every decision starts with what is best for your callers.' },
  { icon: ShieldCheck,  title: 'Professionalism',     desc: 'Polished, articulate, and completely aligned with your brand.' },
  { icon: HeartHandshake, title: 'Teamwork',           desc: 'We act as a seamless extension of your own staff.' },
  { icon: Target,       title: 'Integrity',           desc: 'Honest reporting, clear pricing, and no hidden fees.' },
  { icon: Lightbulb,    title: 'Innovation',          desc: 'Always improving our technology to serve you better.' },
  { icon: Heart,        title: 'Empathy',             desc: 'Handling every call with genuine care and understanding.' },
];

const AboutPage = () => (
  <div className="pb-12">
    {/* Hero */}
    <section className="bg-slate-900 text-white py-16 md:py-32 relative overflow-hidden">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-cygnet-light font-medium text-sm mb-6"
        >
          Trusted since 2000 — over 26 years of UK call handling
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 leading-tight"
        >
          We are not a call centre.<br className="hidden md:block" />
          <span className="text-cygnet-light"> We are your team.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-lg md:text-2xl font-semibold text-white/80 italic"
        >
          We listen. We care. We deliver.
        </motion.p>
      </div>
    </section>

    {/* Mission */}
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="text-sm font-bold text-cygnet-dark uppercase tracking-wider mb-4">Our Mission</h2>
          <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-8">
            "We exist so that no UK business ever loses a client because a phone went unanswered."
          </p>
          <div className="w-24 h-1 bg-cygnet-dark mx-auto rounded-full" />
        </motion.div>
      </div>
    </section>

    {/* Team image */}
    <section className="pb-16 md:pb-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden h-64 md:h-96"
        >
          <img
            src="https://images.pexels.com/photos/8937607/pexels-photo-8937607.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            alt="Cygnet Call Centre team"
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/65" />
          <div className="absolute inset-0 flex items-center justify-center text-center text-white p-8">
            <blockquote className="text-2xl md:text-3xl font-bold italic max-w-2xl">
              "No missed calls. No lost leads. Just a seamless experience your customers will remember."
            </blockquote>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Stats */}
    <section className="py-12 md:py-16 bg-gray-50 border-y border-gray-200">
      <div className="mx-auto max-w-5xl px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { value: <AnimatedCounter target={26} suffix="+" />, label: 'Years Experience', sub: 'Perfecting the art of professional communication since 2000.' },
            { value: '100%', label: 'UK-Based Team', sub: 'All operators based in the United Kingdom.' },
            { value: '16+', label: 'Industries Served', sub: 'Specialised knowledge across sectors.' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`text-center p-6 ${i === 1 ? 'border-y md:border-y-0 md:border-x border-gray-200' : ''}`}
            >
              <div className="text-5xl font-black text-cygnet-dark mb-2">{stat.value}</div>
              <div className="text-xl font-bold text-gray-900 mb-2">{stat.label}</div>
              <p className="text-gray-500 text-sm">{stat.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Core Values.</h2>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              When your customers call, they shouldn't feel like they've hit a sterile, robotic answering service. They should feel like they've reached a helpful, knowledgeable member of your in-house team.
            </p>
            <p className="text-lg text-gray-500 leading-relaxed">
              Our operations are guided by six core values that ensure we always deliver an exceptional experience for you and your callers.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border border-gray-200 p-6 rounded-2xl"
              >
                <div className="w-12 h-12 bg-cygnet-dark/10 text-cygnet-dark rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-500 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Mission detail */}
    <section className="py-16 md:py-24 bg-gray-50 border-y border-gray-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <p className="text-xl text-gray-500 leading-8 mb-6">
            Our mission is to empower businesses of all sizes by providing reliable, efficient, and cost-effective call centre services, enabling them to connect with their customers and drive growth.
          </p>
          <p className="text-base text-gray-600 leading-7 mb-4">
            Incorporated in 2000, Cygnet has spent more than 26 years perfecting the craft of professional UK call handling. The principle that has guided us from day one is simple: <span className="font-semibold text-gray-900">we listen, we care, we deliver.</span>
          </p>
          <p className="text-base text-gray-600 leading-7 mb-4">
            We strive to deliver the highest level of customer service, utilising our expertise and state-of-the-art technology to provide customised solutions that meet the unique needs of our clients.
          </p>
          <p className="text-base text-gray-600 leading-7">
            Our ISO9001 certification ensures that all of our solutions meet the highest international standards, while our ICO certification guarantees the protection of your data.
          </p>
        </div>
      </div>
    </section>

    {/* FAQ teaser */}
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-10">Frequently Asked Questions</h2>
        {[
          { q: 'What services does Cygnet Call Centre offer?', a: 'Cygnet Call Centre offers a range of services including live call answering, call screening and forwarding, message taking, appointment scheduling, fault logging, duty of care calls, and more.' },
          { q: 'Are your services available 24/7?', a: 'Yes, our call centre services are available 24/7, 365 days a year to ensure that your business is always connected with your customers.' },
          { q: 'How do you ensure data security?', a: 'We are ISO9001 and ICO certified, ensuring that all our processes meet the highest international standards and that your data is protected with the utmost security.' },
          { q: 'Can you customize your services to fit my business needs?', a: 'Absolutely. We provide customized solutions tailored to meet the specific needs of your business, ensuring that our services align perfectly with your objectives.' },
        ].map((faq) => (
          <div key={faq.q} className="border-t border-gray-200 pt-6 pb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">{faq.q}</h3>
            <p className="text-gray-500 leading-7">{faq.a}</p>
          </div>
        ))}
        <div className="mt-4">
          <Link to="/faq" className="text-cygnet-dark font-medium hover:underline inline-flex items-center gap-2">
            See all FAQs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 md:py-24 bg-cygnet-dark text-white text-center">
      <div className="mx-auto max-w-3xl px-6">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to upgrade your availability?</h2>
        <p className="text-xl text-white/80 mb-8">
          Partner with Cygnet and ensure every interaction with your business is a premium experience.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-white text-cygnet-dark px-8 py-4 text-lg font-bold hover:bg-gray-100 transition-colors">
          Get in Touch <ArrowRight className="ml-2 w-5 h-5" />
        </Link>
      </div>
    </section>
  </div>
);

export default AboutPage;
